import { useState, useEffect, useCallback } from 'react'
import { api, UserProfile, OnboardingStatus } from '../api/client'

interface UseOnboardingReturn {
    profile: UserProfile | null
    onboardingStatus: OnboardingStatus | null
    isLoading: boolean
    shouldShowWizard: boolean
    shouldShowStealthIraPrompt: boolean
    refetch: () => Promise<void>
}

export function useOnboarding(): UseOnboardingReturn {
    const [profile, setProfile] = useState<UserProfile | null>(null)
    const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const fetchData = useCallback(async () => {
        try {
            const [profileData, statusData] = await Promise.all([
                api.getUserProfile(),
                api.getOnboardingStatus(),
            ])
            setProfile(profileData)
            setOnboardingStatus(statusData)
        } catch {
            // Silently fail — user may not be authenticated yet
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const shouldShowWizard =
        !isLoading &&
        profile !== null &&
        !profile.onboardingCompleted &&
        !profile.onboardingDismissed

    const shouldShowStealthIraPrompt =
        !isLoading && onboardingStatus !== null && onboardingStatus.stealthIraPromptDue

    return {
        profile,
        onboardingStatus,
        isLoading,
        shouldShowWizard,
        shouldShowStealthIraPrompt,
        refetch: fetchData,
    }
}
